import ButtonLink from '../ButtonLink';
import EmailSent from '../EmailSent';
import Heading from '@atlaskit/heading';
import OtpInput, { OtpInputHandle } from '../OtpInput';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import TallButton from '../TallButton';
import Whitebox from '../WhiteboxPage';
import messages from './VerifyEmailOtp.messages';
import { FormattedMessage, useIntl } from 'react-intl';
import PageTitle from '../PageTitle';
import { OTP_CODE_FULL_LENGTH } from './constants';
import { captureErrorMessage } from '../../utilities/analytics/error-reporting';
import Form, { ErrorMessage, Field, MessageWrapper } from '@atlaskit/form';
import { Stack } from '@atlaskit/primitives';
import { cssMap } from '@atlaskit/css';

type VerifyEmailOtpFlow = 'sign-up' | 'login-challenge';

export type VerifyEmailOtpErrorCode =
  | 'invalid_otp'
  | 'invalid_otp_length'
  | 'invalid_token'
  | 'expired_token'
  | 'rate_limited'
  | 'unexpected_error';

export type VerifyEmailOtpProps = {
  pageId: string;
  flow: VerifyEmailOtpFlow;
  email: string;
  isLoading: boolean;
  errorCode?: VerifyEmailOtpErrorCode;
  additionalWhiteboxFooter?: React.ReactNode;
  onVerifyEmailOtp: (otp: string) => void;
  onResendEmailOtp: () => void;
  onInvalidEmailOtpLength?: (otp: string) => void;
};

const styles = cssMap({
  otpInputWrapper: {
    display: 'flex',
    alignContent: 'center',
    justifyContent: 'center',
  },
});

const EMPTY_OTP_STATE = ' '.repeat(OTP_CODE_FULL_LENGTH);

const removePlaceholders = (otp: string) => otp.replace(/ /g, '');

export const VerifyEmailOtp = ({
  pageId,
  flow,
  email,
  isLoading,
  errorCode,
  additionalWhiteboxFooter: footer,
  onVerifyEmailOtp,
  onResendEmailOtp,
  onInvalidEmailOtpLength,
}: VerifyEmailOtpProps) => {
  const [otp, setOtp] = useState<string>(EMPTY_OTP_STATE);
  const [isInvalidOtpLength, setInvalidOtpLength] = useState<boolean>(false);
  const otpInputRef = useRef<OtpInputHandle>(null);

  const { formatMessage } = useIntl();

  const handleOtpChange = (otp: string) => {
    setOtp(otp);
    const sanitisedOtp = removePlaceholders(otp);
    if (sanitisedOtp.length === OTP_CODE_FULL_LENGTH) {
      setInvalidOtpLength(false);
      onVerifyEmailOtp(sanitisedOtp);
    }
  };

  const handleSubmit = () => {
    const sanitisedOtp = removePlaceholders(otp);
    if (sanitisedOtp.length === OTP_CODE_FULL_LENGTH) {
      setInvalidOtpLength(false);
      onVerifyEmailOtp(sanitisedOtp);
    } else {
      setInvalidOtpLength(true);
      onInvalidEmailOtpLength?.(sanitisedOtp);
    }
  };

  const otpMessageDescriptor = useMemo(() => {
    switch (flow) {
      case 'sign-up':
        return messages.signUpOtpMessage;
      case 'login-challenge':
        return messages.loginChallengeOtpMessage;
      default:
        captureErrorMessage(
          `No OTP message defined for VerifyEmailOtp flow "${flow}", falling back to default message`
        );
        return messages.defaultOtpMessage;
    }
  }, [flow]);

  const errorMessageDescriptor = useMemo(() => {
    if (isInvalidOtpLength) {
      return messages.invalidOtpLengthErrorMessage;
    }
    if (!errorCode) {
      return undefined;
    }

    switch (errorCode) {
      case 'invalid_otp':
        return messages.invalidOtpErrorMessage;
      case 'invalid_otp_length':
        return messages.invalidOtpLengthErrorMessage;
      case 'invalid_token':
        return messages.invalidTokenErrorMessage;
      case 'expired_token':
        return messages.expiredTokenErrorMessage;
      case 'rate_limited':
        return messages.rateLimitedErrorMessage;
      case 'unexpected_error':
        return messages.unexpectedErrorErrorMessage;
      default:
        return messages.unexpectedErrorErrorMessage;
    }
  }, [errorCode, isInvalidOtpLength]);

  useEffect(() => {
    if (errorCode === 'invalid_otp') {
      setOtp(EMPTY_OTP_STATE);
    }
  }, [errorCode]);

  // Return focus to the OTP input on error. The error itself is announced by
  // the MessageWrapper live region, so it is not associated with the fields.
  useEffect(() => {
    if (errorMessageDescriptor) {
      otpInputRef.current?.focus();
    }
  }, [errorMessageDescriptor, errorCode, isInvalidOtpLength]);

  return (
    <Whitebox
      pageId={pageId}
      header={
        <Heading as="h1" size="medium">
          <FormattedMessage {...messages.headOtpTitle} />
        </Heading>
      }
      whiteboxFooter={
        <>
          <ButtonLink id="otp-resend" spacing="none" onClick={onResendEmailOtp}>
            {formatMessage(messages.resendOtpButton)}
          </ButtonLink>
          {footer}
        </>
      }>
      <PageTitle title={formatMessage(messages.headOtpTitle)} />
      <EmailSent
        email={email}
        message={formatMessage(otpMessageDescriptor)}
        withEmailSentImage={false}
      />
      <Form onSubmit={handleSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <Field name="otp-input">
              {() => (
                <Stack xcss={styles.otpInputWrapper}>
                  <OtpInput
                    ref={otpInputRef}
                    legend={formatMessage(messages.otpInputGroupLabel)}
                    otpCodeFullLength={OTP_CODE_FULL_LENGTH}
                    onChange={handleOtpChange}
                    otp={otp}
                    isLoading={isLoading}
                    isError={!!errorMessageDescriptor}
                  />
                  <MessageWrapper>
                    {!!errorMessageDescriptor && (
                      <ErrorMessage>{formatMessage(errorMessageDescriptor)}</ErrorMessage>
                    )}
                  </MessageWrapper>
                </Stack>
              )}
            </Field>
            <TallButton
              id="otp-submit"
              appearance="primary"
              type="submit"
              isFullWidth
              style={{ marginTop: '30px' }}
              isLoading={isLoading}>
              {formatMessage(messages.submitButton)}
            </TallButton>
          </form>
        )}
      </Form>
    </Whitebox>
  );
};

export default VerifyEmailOtp;
